/**
 * 菜单权限控制工具函数
 */

// 菜单代码映射表 - 将路由名称映射到菜单代码
export const MENU_CODE_MAP: Record<string, string> = {
  // 主菜单
  customer: 'customer',
  report: 'report',
  operations: 'operations',
  finance: 'finance',
  system: 'system',
  activity: 'activity',
  platform: 'platform',
  task: 'task',
  netValueSolution: 'netValueSolution',
  // 净值方案子菜单：使用独立菜单编码
  netValueSolutionCommonParams: 'netValueSolutionCommonParams',
  netValueSolutionList: 'netValueSolutionList',
  netValueSolutionAdd: 'netValueSolutionList',
  // 报告模板配置：独立一级菜单
  reportTemplate: 'reportTemplate',
  reportTemplateList: 'reportTemplateList',
  reportTemplateCommonParams: 'reportTemplateCommonParams',
  reportTemplateDetail: 'reportTemplateList',
  reportTemplateAdd: 'reportTemplateList',
  ledger: 'ledger',
  inquiryBatch: 'inquiryBatch',

  // 客户管理子菜单
  customerAdd: 'customerAdd',
  customerEdit: 'customerEdit',
  customerView: 'customerView',

  // 系统管理子菜单
  organization: 'systemOrganization',
  dept: 'systemDept',
  district: 'systemDistrict',
  businessSubType: 'systemBusinessSubType',
  formProfile: 'systemFormProfile',
  formProfileDetail: 'systemFormProfile',
  certRecognizeEval: 'systemCertRecognizeEval',
  personal: 'systemPersonal',
  settings: 'settings',
  user: 'systemUser',
  role: 'systemRole',
  permission: 'systemPermission',
  menu: 'systemMenu',
  logs: 'systemLogs',

  // 任务管理子菜单
  taskMyTask: 'taskMyTask',
  taskStatistics: 'taskStatistics',
  taskSupplement: 'taskSupplement',

  // 报告管理子菜单
  reportReview: 'reportReview',
  reportStatistics: 'reportStatistics',

  // 台账管理子菜单
  ledgerList: 'ledgerList',
  ledgerInquiry: 'ledgerInquiry',

  // 直接菜单代码映射（用于access.ts中的直接调用）
  systemOrganization: 'systemOrganization',
  systemDept: 'systemDept',
  systemDistrict: 'systemDistrict',
  systemBusinessSubType: 'systemBusinessSubType',
  systemFormProfile: 'systemFormProfile',
  systemCertRecognizeEval: 'systemCertRecognizeEval',
  systemPersonal: 'systemPersonal',
  systemUser: 'systemUser',
  systemRole: 'systemRole',
  systemPermission: 'systemPermission',
  systemMenu: 'systemMenu',
  systemLogs: 'systemLogs',
};

/**
 * 检查用户是否有访问指定菜单的权限
 * @param menuName 菜单名称（路由配置中的name字段）
 * @param userMenuCodes 用户拥有的菜单权限代码数组
 * @param userId 用户ID，用于判断是否为超级管理员
 * @returns 是否有权限
 */
export function hasMenuPermission(
  menuName: string,
  userMenuCodes: string[],
  userId?: string,
): boolean {
  // 个人设置菜单对所有用户开放
  if (menuName === 'personal') {
    return true;
  }

  // 报告详情页：我的审核或报告统计任一权限即可访问（前端复合权限，无单独菜单代码）
  if (menuName === 'reportDetail') {
    return (
      hasMenuPermission('reportReview', userMenuCodes, userId) ||
      hasMenuPermission('reportStatistics', userMenuCodes, userId)
    );
  }

  // ID为1的用户（超级管理员）对所有菜单都有权限
  if (userId === '1') {
    return true;
  }

  if (!menuName || !userMenuCodes || userMenuCodes.length === 0) {
    return false;
  }

  // 获取菜单对应的代码
  const menuCode = MENU_CODE_MAP[menuName];
  if (!menuCode) {
    console.warn(`未找到菜单 "${menuName}" 对应的菜单代码`);
    return false;
  }

  // 检查用户是否拥有该菜单权限
  return userMenuCodes.includes(menuCode);
}

/**
 * 过滤路由配置，只保留用户有权限访问的菜单
 * @param routes 原始路由配置
 * @param userMenuCodes 用户拥有的菜单权限代码数组
 * @param userId 用户ID，用于判断是否为超级管理员
 * @returns 过滤后的路由配置
 */
export function filterRoutesByPermission(
  routes: any[],
  userMenuCodes: string[],
  userId?: string,
): any[] {
  // 即使没有菜单权限，也要保留系统管理菜单（因为包含个人设置）
  // if (!userMenuCodes || userMenuCodes.length === 0) {
  //   return [];
  // }

  return routes
    .map((route) => {
      // 如果是登录页面等不需要权限的页面，直接保留
      if (route.layout === false || !route.name) {
        return route;
      }

      // 检查当前路由是否有权限
      const hasPermission = hasMenuPermission(
        route.name,
        userMenuCodes,
        userId,
      );

      // 如果有子路由，先递归过滤子路由
      if (route.routes && route.routes.length > 0) {
        const filteredSubRoutes = filterRoutesByPermission(
          route.routes,
          userMenuCodes,
          userId,
        );

        // 如果父菜单没有权限，但子菜单有权限，则保留父菜单
        if (!hasPermission && filteredSubRoutes.length > 0) {
          return {
            ...route,
            routes: filteredSubRoutes,
          };
        }

        // 如果父菜单有权限，保留父菜单和过滤后的子菜单
        if (hasPermission) {
          return {
            ...route,
            routes: filteredSubRoutes,
          };
        }

        // 如果父菜单和子菜单都没有权限，过滤掉
        return null;
      }

      // 没有子路由的情况，直接检查权限
      if (!hasPermission) {
        return null; // 没有权限，过滤掉
      }

      return route;
    })
    .filter((route) => route !== null); // 过滤掉null值
}

/**
 * 从用户信息中提取菜单权限代码
 * @param userInfo 用户信息对象
 * @returns 菜单权限代码数组
 */
export function extractUserMenuCodes(userInfo: any): string[] {
  if (!userInfo || !userInfo.roles || !Array.isArray(userInfo.roles)) {
    return [];
  }

  // 从所有角色中收集菜单权限代码
  const menuCodes = new Set<string>();

  userInfo.roles.forEach((role: any) => {
    if (role.sysMenuCode && Array.isArray(role.sysMenuCode)) {
      role.sysMenuCode.forEach((code: string) => {
        menuCodes.add(code);
      });
    }
  });

  return Array.from(menuCodes);
}

/**
 * 获取用户菜单权限的调试信息
 * @param userInfo 用户信息对象
 * @returns 调试信息对象
 */
export function getMenuPermissionDebugInfo(userInfo: any) {
  const menuCodes = extractUserMenuCodes(userInfo);

  return {
    userInfo,
    extractedMenuCodes: menuCodes,
    menuCodeMap: MENU_CODE_MAP,
    availableMenus: Object.keys(MENU_CODE_MAP).filter((menuName) =>
      hasMenuPermission(menuName, menuCodes),
    ),
  };
}
